import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import {
  makeStyles,
  Popover,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faEye } from "@fortawesome/free-solid-svg-icons";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import { grayColor } from "../../../../../assets/jss/material-dashboard-pro-react";

const columnVisibilityControlsStyle = (theme, clientColor) => ({
  columnControlsContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  toggleButton: {
    padding: "8px 12px",
    fontSize: "11px",
    backgroundColor: grayColor[13],
    border: `1px solid ${grayColor[10]}`,
    borderRadius: "4px",
    color: grayColor[2],
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    minHeight: "32px",
    minWidth: "140px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[12],
      borderColor: clientColor,
      color: clientColor,
    },
  },
  popoverContent: {
    padding: "16px",
    minWidth: "700px",
    maxWidth: "800px",
    maxHeight: "450px",
    overflow: "auto",
  },
  nodeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "16px",
    marginTop: "12px",
  },
  popoverTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: grayColor[1],
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  nodeSection: {
    marginBottom: "16px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  nodeTitle: {
    fontSize: "12px",
    fontWeight: "600",
    color: clientColor,
    marginBottom: "8px",
    padding: "4px 8px",
    backgroundColor: `${clientColor}15`,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  nodeToggle: {
    fontSize: "10px",
    cursor: "pointer",
    padding: "2px",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
      borderRadius: "2px",
    },
  },
  columnList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingLeft: "8px",
  },
  columnItem: {
    "& .MuiFormControlLabel-root": {
      margin: 0,
      width: "100%",
    },
    "& .MuiFormControlLabel-label": {
      fontSize: "11px",
      color: grayColor[2],
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiCheckbox-root": {
      padding: "4px",
      color: grayColor[6],
      "&.Mui-checked": {
        color: clientColor,
      },
    },
  },
  summaryText: {
    fontSize: "10px",
    color: grayColor[4],
    fontStyle: "italic",
  },
  selectAllContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    padding: "8px",
    backgroundColor: grayColor[13],
    borderRadius: "4px",
  },
  selectAllButton: {
    fontSize: "10px",
    padding: "4px 8px",
    backgroundColor: "transparent",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "3px",
    color: grayColor[2],
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: clientColor,
      color: "white",
      borderColor: clientColor,
    },
  },
});

const ColumnVisibilityControls = ({
  visibleColumns,
  onColumnVisibilityChange,
  nodeStructure,
  metadata,
  columnOrganization,
  visibleNodes,
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) =>
    columnVisibilityControlsStyle(theme, clientColor),
  );
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const handleToggleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (columnKey) => {
    onColumnVisibilityChange(columnKey, !visibleColumns[columnKey]);
  };

  const handleSelectAll = () => {
    const allColumns = {};
    Object.keys(nodeStructure).forEach((nodeKey) => {
      const nodeMetrics = metadata?.nodes?.[nodeKey]?.columns || [];
      nodeMetrics.forEach((metric) => {
        if (metric.calculation_type === "delta_rate") {
          // Delta columns only have one column
          const deltaKey = `delta_${nodeKey}_${metric.column_id}`;
          allColumns[deltaKey] = true;
        } else {
          // Regular columns have both scenarios
          const asIsKey = `asis_${nodeKey}_${metric.column_id}`;
          const toBeKey = `tobe_${nodeKey}_${metric.column_id}`;
          allColumns[asIsKey] = true;
          allColumns[toBeKey] = true;
        }
      });
    });

    Object.keys(allColumns).forEach((columnKey) => {
      onColumnVisibilityChange(columnKey, true);
    });
  };

  const handleDeselectAll = () => {
    Object.keys(visibleColumns).forEach((columnKey) => {
      onColumnVisibilityChange(columnKey, false);
    });
  };

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(Boolean).length;
  };

  const getTotalColumnCount = () => {
    let total = 0;
    Object.keys(nodeStructure).forEach((nodeKey) => {
      const nodeMetrics = metadata?.nodes?.[nodeKey]?.columns || [];
      nodeMetrics.forEach((metric) => {
        if (metric.calculation_type === "delta_rate") {
          total += 1; // Delta columns only have one column
        } else {
          total += 2; // Regular columns have AS IS and TO BE
        }
      });
    });
    return total;
  };

  const isOpen = Boolean(anchorEl);

  return (
    <div className={classes.columnControlsContainer}>
      <div className={classes.toggleButton} onClick={handleToggleClick}>
        <FontAwesomeIcon icon={faEye} />
        <span>
          Columns ({getVisibleColumnCount()}/{getTotalColumnCount()})
        </span>
        <FontAwesomeIcon icon={faChevronDown} />
      </div>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div className={classes.popoverContent}>
          <div className={classes.popoverTitle}>
            <FontAwesomeIcon icon={faEye} />
            Column Visibility
          </div>

          <div className={classes.selectAllContainer}>
            <span className={classes.summaryText}>
              {getVisibleColumnCount()} of {getTotalColumnCount()} columns
              visible
            </span>
            <div>
              <button
                className={classes.selectAllButton}
                onClick={handleSelectAll}
              >
                Select All
              </button>
              <button
                className={classes.selectAllButton}
                onClick={handleDeselectAll}
                style={{ marginLeft: "4px" }}
              >
                Deselect All
              </button>
            </div>
          </div>

          <div className={classes.nodeGrid}>
            {Object.keys(nodeStructure).map((nodeKey) => {
              const nodeData = nodeStructure[nodeKey];
              const nodeMetrics = metadata?.nodes?.[nodeKey]?.columns || [];

              return (
                <div key={nodeKey} className={classes.nodeSection}>
                  <div className={classes.nodeTitle}>
                    <span>{nodeData.name}</span>
                  </div>

                  <div className={classes.columnList}>
                    {nodeMetrics.map((metric) => {
                      // Handle delta_rate columns differently - they only have delta values
                      if (metric.calculation_type === "delta_rate") {
                        const deltaKey = `delta_${nodeKey}_${metric.column_id}`;
                        const isNodeVisible = visibleNodes[nodeKey] || false;
                        return (
                          <div
                            key={metric.column_id}
                            className={classes.columnItem}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={visibleColumns[deltaKey] || false}
                                  onChange={() => handleColumnToggle(deltaKey)}
                                  disabled={!isNodeVisible}
                                  size="small"
                                />
                              }
                              label={`${metric.column_name} Δ%`}
                              style={{
                                opacity: isNodeVisible ? 1 : 0.5,
                              }}
                            />
                          </div>
                        );
                      } else if (columnOrganization === "scenario-first") {
                        // Scenario-first: Show AS IS and TO BE for each metric
                        const asIsKey = `asis_${nodeKey}_${metric.column_id}`;
                        const toBeKey = `tobe_${nodeKey}_${metric.column_id}`;
                        const isNodeVisible = visibleNodes[nodeKey] || false;

                        return (
                          <div key={metric.column_id}>
                            <div className={classes.columnItem}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={visibleColumns[asIsKey] || false}
                                    onChange={() => handleColumnToggle(asIsKey)}
                                    disabled={!isNodeVisible}
                                    size="small"
                                  />
                                }
                                label={`AS IS - ${metric.column_name}`}
                                style={{
                                  opacity: isNodeVisible ? 1 : 0.5,
                                }}
                              />
                            </div>
                            <div className={classes.columnItem}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={visibleColumns[toBeKey] || false}
                                    onChange={() => handleColumnToggle(toBeKey)}
                                    disabled={!isNodeVisible}
                                    size="small"
                                  />
                                }
                                label={`TO BE - ${metric.column_name}`}
                                style={{
                                  opacity: isNodeVisible ? 1 : 0.5,
                                }}
                              />
                            </div>
                          </div>
                        );
                      } else {
                        // Elo-first: Show metric with both scenarios
                        const asIsKey = `asis_${nodeKey}_${metric.column_id}`;
                        const toBeKey = `tobe_${nodeKey}_${metric.column_id}`;
                        const bothVisible =
                          visibleColumns[asIsKey] && visibleColumns[toBeKey];
                        const someVisible =
                          visibleColumns[asIsKey] || visibleColumns[toBeKey];
                        const isNodeVisible = visibleNodes[nodeKey] || false;

                        return (
                          <div
                            key={metric.column_id}
                            className={classes.columnItem}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={bothVisible}
                                  indeterminate={someVisible && !bothVisible}
                                  onChange={() => {
                                    handleColumnToggle(asIsKey);
                                    handleColumnToggle(toBeKey);
                                  }}
                                  disabled={!isNodeVisible}
                                  size="small"
                                />
                              }
                              label={metric.column_name}
                              style={{
                                opacity: isNodeVisible ? 1 : 0.5,
                              }}
                            />
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Popover>
    </div>
  );
};

ColumnVisibilityControls.propTypes = {
  visibleColumns: PropTypes.object.isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
  nodeStructure: PropTypes.object.isRequired,
  metadata: PropTypes.object,
  columnOrganization: PropTypes.oneOf(["scenario-first", "elo-first"])
    .isRequired,
  visibleNodes: PropTypes.object.isRequired,
};

export default ColumnVisibilityControls;
